import GameObjectType from '../../GameObject/GameObject';
import Container from '../../Prototype/Container';
import Sprite from '../../Prototype/Sprite';
import Text from '../../Prototype/Text';
import TransformComponent from '../../Component/Transform';
import Renderable from '../../Component/Renderable';
import Interactable from '../../Component/Interactable';
import NineSlice from '../../Prototype/NineSlice';
import Game from '../../Game';
import Graphics from '../../Prototype/Graphics';
import './../PixiText';
import {
    ArmatureModel,
    ContainerModel,
    GraphicsCommandType,
    GraphicsModel,
    HitAreaType,
    Model,
    ModelType,
    NineSlicePlaneModel,
    Raw,
    SpriteModel,
    TextModel,
} from './types';
import GameObject from '../../GameObject/GameObject';
import Armature from "Prototype/Armature";

// All layouts context
const layoutsContext = require.context('../../Layouts', true);
const deg2rad = (deg: number) => (deg / 180) * Math.PI;

/**
 * Populates layout object regarding its type in generated layout file
 *
 * @param layout - An empty object, that is instance of Layout class. Will be mutated (populated) in this function, to match its type.
 * @param raw - Raw layout, that contains objects properties value, to populate layout with.
 * @return [void] - layout is mutated.
 * */
export const populate = (layout: any, raw:any) => {
    const viewById: Record<string, GameObject> = {};
    const nestedLayoutById: Record<string, any> = {};

    // In this cycle all gameObjects are created and assigned to viewById record
    for (const model of raw.list) {
        let view: GameObject;

        if (model.type === ModelType.Layout) {
            const data = createNestedLayout(model);
            view = data.root;
            nestedLayoutById[model.id] = data.layout;
        } else {
            view = createView(model);
        }

        // if model has any data try to parse is as json and assign to gameObject
        // if parsing fails, assign unparsed value instead
        try {
            view.layoutData = JSON.parse(model.data);
        } catch (e) {
            view.layoutData = model.data;
        }

        viewById[model.id] = view;
        viewById[model.parent]?.addChild(view);
        addComponents(view, model);
    }

    // Assign created gameObjects to layout properties, regarding their name, className and nameSpace
    fillNameSpaces({ layout, raw, viewById, nestedLayoutById });
};

type Scope = Record<string, /*GameObject | GameObject[]*/any> & { children: GameObject[] };
type Item = { model: Model; parent: Item | null; children: Item[] };

/**
 * Assign created gameObjects (in viewById) to layout, regarding their name, className and nameSpace.
 *
 * @return [void] Mutates layout param
 * */
function fillNameSpaces(props: {
    layout: any;
    raw: Raw;
    viewById: Record<string, GameObject>;
    nestedLayoutById: Record<string, any>;
}): void {
    const { layout, raw, viewById, nestedLayoutById } = props;
    const { tree } = toTree(raw.list);
    const scope = layout as unknown as Scope;

    const traverse = (item: Item, pScope: Scope) => {
        const view = viewById[item.model.id];
        const nestedLayout = nestedLayoutById[item.model.id];
        const object = nestedLayout ?? view;

        if (item.model.name) {
            pScope[item.model.name] = object;
        }

        if (item.model.className) {
            pScope[item.model.className] = pScope[item.model.className] || [];
            pScope[item.model.className].push(object);
        }

        let scope = pScope;

        if (item.model.nameSpace) {
            scope = { children: view.children };

            if (item.model.nameSpace.endsWith('[]')) {
                const n = item.model.nameSpace.slice(0, -2);
                pScope[n] = pScope[n] || [];
                pScope[n].push(scope);
            } else {
                pScope[item.model.nameSpace] = scope;
            }
        }

        item.children.forEach((c) => {
            traverse(c, scope);
        });
    };

    traverse(tree, scope);
}

/**
 * An util method that allows to transform raw layout models list into tree structure
 * */
function toTree(list: Model[]): { tree: Item; itemById: { [key: string]: Item } } {
    const itemById: { [key: string]: Item } = {};
    const parentByItemId: { [key: string]: Item } = {};
    let tree!: Item;

    for (const model of list) {
        const item: Item = { model, parent: null, children: [] };
        const parentItem = itemById[item.model.parent];

        if (parentItem) {
            item.parent = parentItem;
            parentItem.children.push(item);
        }

        itemById[item.model.id] = item;
        parentByItemId[item.model.id] = parentItem;

        if (!tree) {
            tree = item;
        }
    }

    return { tree, itemById };
}

const addComponents = (view: GameObject, model: Model): void => {
    view.addComponent(TransformComponent);
    view.addComponent(Renderable);
    view.getComponent(TransformComponent).position.copy(view.container.position);

    if (model.interactive) {
        view.addComponent(Interactable);
        view.getComponent(Interactable).enabled = true;
    }

    if (model.buttonMode) {
        view.addComponent(Interactable);
        view.getComponent(Interactable).buttonMode = true;
    }
};

/**
 * Create gameObject. Regarding model type. Copy model properties to pixi object.
 *
 * @param model - raw object.
 * @return [GameObject] - The created object with assigned model values to it. But without components and parent.
 * */
export const createView = (model: any): GameObject => {
    let view: GameObject;

    switch (model.type) {
        case ModelType.Container:
            view = GameObjectType.Instantiate(Container, {});
            copyContainerProps(view.container, model);
            break;

        case ModelType.Sprite:
            view = GameObjectType.Instantiate(Sprite, {});
            copySpriteProps(view.container as PIXI.Sprite, model);

            break;

        case ModelType.Text:
            view = GameObjectType.Instantiate(Text, {});
            copyTextProps(view.container as PIXI.Text, model);

            break;

        case ModelType.NineSlicePlane:
            view = GameObjectType.Instantiate(NineSlice, {});
            copyNineSlicePlaneProps(view.container as PIXI.NineSlicePlane, model);
            break;

        case ModelType.Graphics:
            view = GameObjectType.Instantiate(Graphics, {});
            copyGraphicsProps(view.container as PIXI.Graphics, model);
            break;

        case ModelType.Armature:
            view = instantiateArmature(model);
            break;

        default:
            console.error(`Unknown type ${model.type}`);
            break;
    }

    setHitArea(view.container, model);

    return view;
};

/**
 * Create nested layout. Use webpack layoutsContext to get nested layout model, and populate ir the same way as current.
 *
 * @param model - raw object with Layout type
 * @return - populated layout and its root object (as it can be not accessible via layout.root if the name is different)
 * */
const createNestedLayout = (
    model: any,
): { layout: object; root: GameObject } => {

    // TODO: This needs to be refactored to either guarantee dependency availability (pre-loading) or asynchronous loading.

    // Format the key to be compatible with the keys provided with `require.context`.
    const key = model.path.replace('src/Layouts', '.');

    // Parse the class name which hopefully matches the exported class name for the layout.
    const className = model.path.slice(model.path.lastIndexOf("/") + 1).replace(".ts", "");

    // Get the class from the module.
    const layoutClass = layoutsContext(key)[className];

    // Create the layout.
    const layout = new layoutClass();

    const rootView = layout[layoutClass.raw.list[0].name] as GameObject;

    copyContainerProps(rootView.container, model);

    return { layout, root: rootView };
};

export const copyContainerProps = (
    container: PIXI.Container,
    model: ContainerModel,
): void => {
    container.name = model.name;
    container.x = model.x;
    container.y = model.y;
    container.scale.x = model.scale.x;
    container.scale.y = model.scale.y;
    container.pivot.x = model.pivot.x;
    container.pivot.y = model.pivot.y;
    container.rotation = deg2rad(model.angle);
    container.alpha = model.alpha;
    container.visible = model.visible;
};

export const copySpriteProps = (
    sprite: PIXI.Sprite,
    model: SpriteModel,
): void => {
    sprite.name = model.name;
    sprite.x = model.x;
    sprite.y = model.y;
    sprite.anchor.x = model.anchor.x;
    sprite.anchor.y = model.anchor.y;
    sprite.scale.x = model.scale.x;
    sprite.scale.y = model.scale.y;
    sprite.rotation = deg2rad(model.angle);
    sprite.alpha = model.alpha;
    sprite.visible = model.visible;
    sprite.tint = model.tint;
    sprite.blendMode = model.blendMode;

    if (model.texture && model.texture in PIXI.utils.TextureCache) {
        sprite.texture = PIXI.Texture.from(model.texture);
    }
};

export const copyTextProps = (text: PIXI.Text, model: TextModel): void => {
    text.name = model.name;
    text.x = model.x;
    text.y = model.y;
    text.anchor.x = model.anchor.x;
    text.anchor.y = model.anchor.y;
    text.scale.x = model.scale.x;
    text.scale.y = model.scale.y;
    text.rotation = deg2rad(model.angle);
    text.alpha = model.alpha;
    text.visible = model.visible;
    text.blendMode = model.blendMode;
    text.maxWidth = model.maxWidth;
    text.maxHeight = model.maxHeight;
    text.maxFontSize = model.maxFontSize;
    text.text = model.localize ? Game.locale.getText(model.text) : model.text;
    text.style = new PIXI.TextStyle(model.style);
};

export const copyGraphicsProps = (
    graphics: PIXI.Graphics,
    model: GraphicsModel,
): void => {
    graphics.name = model.name;
    graphics.x = model.x;
    graphics.y = model.y;
    graphics.scale.x = model.scale.x;
    graphics.scale.y = model.scale.y;
    graphics.pivot.x = model.pivot.x;
    graphics.pivot.y = model.pivot.y;
    graphics.rotation = deg2rad(model.angle);
    graphics.alpha = model.alpha;
    graphics.visible = model.visible;
    graphics.tint = model.tint;
    graphics.blendMode = model.blendMode;

    if (model.commands) {
        for (const command of model.commands) {
            switch (command.type) {
                case GraphicsCommandType.BeginFill:
                    graphics.beginFill(command.color, command.alpha);
                    break;

                case GraphicsCommandType.LineStyle:
                    graphics.lineStyle(
                        command.width,
                        command.color,
                        command.alpha,
                        command.alignment,
                        // command.native
                    );
                    break;

                case GraphicsCommandType.DrawRect:
                    graphics.drawRect(
                        command.x,
                        command.y,
                        command.width,
                        command.height,
                    );
                    break;

                case GraphicsCommandType.DrawRoundedRect:
                    graphics.drawRoundedRect(
                        command.x,
                        command.y,
                        command.width,
                        command.height,
                        command.radius,
                    );
                    break;

                case GraphicsCommandType.DrawCircle:
                    graphics.drawCircle(command.x, command.y, command.radius);
                    break;

                case GraphicsCommandType.DrawEllipse:
                    graphics.drawEllipse(
                        command.x,
                        command.y,
                        command.width,
                        command.height,
                    );
                    break;

                case GraphicsCommandType.DrawStar:
                    graphics.drawStar(
                        command.x,
                        command.y,
                        command.points,
                        command.radius,
                        command.innerRadius,
                        deg2rad(command.rotation),
                    );
                    break;

                case GraphicsCommandType.DrawPolygon:
                    graphics.drawPolygon(
                        command.path.split(/\s*,\s*/).map(Number),
                        );
                    break;

                case GraphicsCommandType.MoveTo:
                    graphics.moveTo(command.x, command.y);
                    break;

                case GraphicsCommandType.LineTo:
                    graphics.lineTo(command.x, command.y);
                    break;

                case GraphicsCommandType.Arc:
                    graphics.arc(
                        command.cx,
                        command.cy,
                        command.radius,
                        deg2rad(command.startAngle),
                        deg2rad(command.endAngle),
                        command.anticlockwise,
                    );
                    break;

                case GraphicsCommandType.ArcTo:
                    graphics.arcTo(
                        command.x1,
                        command.y1,
                        command.x2,
                        command.y2,
                        command.radius,
                    );
                    break;

                case GraphicsCommandType.QuadraticCurveTo:
                    graphics.quadraticCurveTo(
                        command.cpX,
                        command.cpY,
                        command.toX,
                        command.toY,
                    );
                    break;

                case GraphicsCommandType.BezierCurveTo:
                    graphics.bezierCurveTo(
                        command.cpX,
                        command.cpY,
                        command.cpX2,
                        command.cpY2,
                        command.toX,
                        command.toY,
                    );
                    break;

                case GraphicsCommandType.BeginHole:
                    console.warn('Graphics hole is not supported by PIXI 4');
                    // graphics.beginHole();
                    break;

                case GraphicsCommandType.EndHole:
                    // graphics.endHole();
                    break;

                case GraphicsCommandType.ClosePath:
                    graphics.closePath();
                    break;

                case GraphicsCommandType.EndFill:
                    graphics.endFill();
                    break;
            }
        }
    }
};

export const copyNineSlicePlaneProps = (
    nineSlicePlane: PIXI.NineSlicePlane,
    model: NineSlicePlaneModel,
): void => {
    nineSlicePlane.name = model.name;
    nineSlicePlane.x = model.x;
    nineSlicePlane.y = model.y;
    nineSlicePlane.scale.x = model.scale.x;
    nineSlicePlane.scale.y = model.scale.y;
    nineSlicePlane.pivot.x = model.pivot.x;
    nineSlicePlane.pivot.y = model.pivot.y;
    nineSlicePlane.width = model.width;
    nineSlicePlane.height = model.height;
    nineSlicePlane.leftWidth = model.leftWidth;
    nineSlicePlane.topHeight = model.topHeight;
    nineSlicePlane.rightWidth = model.rightWidth;
    nineSlicePlane.bottomHeight = model.bottomHeight;
    nineSlicePlane.rotation = deg2rad(model.angle);
    nineSlicePlane.alpha = model.alpha;
    nineSlicePlane.visible = model.visible;
    nineSlicePlane.tint = model.tint;
    nineSlicePlane.blendMode = model.blendMode;

    if (model.texture in PIXI.utils.TextureCache) {
        nineSlicePlane.texture = PIXI.Texture.from(model.texture);
    }
};

export const instantiateArmature = (
    model: ArmatureModel,
): Armature => {

    const armature = GameObjectType.Instantiate(Armature, {
        ...model
    });

    return armature;
};

const setHitArea = (view: PIXI.Container, model: Model) => {
    const hitArea = model.hitArea;

    if (!hitArea || hitArea.type === HitAreaType.None) return;

    switch (hitArea.type) {
        case HitAreaType.Rectangle:
            view.hitArea = new PIXI.Rectangle(
                hitArea.x,
                hitArea.y,
                hitArea.width,
                hitArea.height,
            );
            break;

        case HitAreaType.Circle:
            view.hitArea = new PIXI.Circle(
                hitArea.x,
                hitArea.y,
                hitArea.radius,
            );
            break;

        case HitAreaType.Ellipse:
            view.hitArea = new PIXI.Ellipse(
                hitArea.x,
                hitArea.y,
                hitArea.halfWidth,
                hitArea.halfHeight,
            );
            break;

        case HitAreaType.Polygon:
            view.hitArea = new PIXI.Polygon(
                hitArea.path.split(/\s*,\s*/).map(Number),
            );
            break;

        case HitAreaType.RoundedRectangle:
            view.hitArea = new PIXI.RoundedRectangle(
                hitArea.x,
                hitArea.y,
                hitArea.width,
                hitArea.height,
                hitArea.radius,
            );
            break;
    }
};
